import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { ShieldCheck, Clock, Lock, Check } from "@phosphor-icons/react";
import { BookingCalendar } from "./BookingCalendar";
import { trackEvent, LEAD_VALUE_CHF, CURRENCY } from "../lib/analytics";
import { buildLeadWhatsAppUrl, trackWhatsAppClick } from "../lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", appointment_date: "", appointment_time: "" };

export const QuickForm = () => {
  const { t } = useTranslation();
  const { ref, inView } = useScrollReveal();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Stable reference for BookingCalendar's `value` prop — prevents inner effects
  // (that depend on value) from re-running on every keystroke.
  const bookingValue = useMemo(
    () => ({ date: form.appointment_date, time: form.appointment_time }),
    [form.appointment_date, form.appointment_time],
  );

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (form.name.trim().length < 2) return t("form.err_name");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return t("form.err_email");
    if (form.phone.trim().length < 6) return t("form.err_phone");
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      message: null,
      appointment_date: form.appointment_date || null,
      appointment_time: form.appointment_time || null,
    };
    // Open a placeholder tab synchronously so mobile Safari / popup blockers
    // let us redirect it to WhatsApp after the async backend call.
    const waWindow = typeof window !== "undefined" ? window.open("", "_blank") : null;
    const waUrl = buildLeadWhatsAppUrl(payload);

    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, payload);
      trackEvent("lead_submit", {
        value: LEAD_VALUE_CHF,
        currency: CURRENCY,
        has_appointment: Boolean(form.appointment_date && form.appointment_time),
        appointment_date: form.appointment_date || null,
        appointment_time: form.appointment_time || null,
        location: typeof window !== "undefined" ? window.location.pathname : "",
      });
      trackWhatsAppClick("quick_form_submit");
      if (waWindow) {
        waWindow.location.href = waUrl;
      } else if (typeof window !== "undefined") {
        window.location.href = waUrl;
      }
      toast.success(t("form.ok_toast"));
      setForm(initial);
      setDone(true);
    } catch (e2) {
      if (waWindow) waWindow.close();
      const msg = e2?.response?.data?.detail || t("form.err_generic");
      toast.error(typeof msg === "string" ? msg : t("form.err_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contato-rapido"
      data-testid="quick-form-section"
      className="relative py-20 lg:py-28 bg-[#2F3F2D] text-[#F6F5F2] overflow-hidden"
    >
      {/* Ambient lighting */}
      <div className="absolute top-0 -right-32 w-[32rem] h-[32rem] rounded-full bg-[#A7CA99]/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 left-1/4 w-[28rem] h-[28rem] rounded-full bg-[#A7CA99]/10 blur-3xl pointer-events-none" />

      <div
        ref={ref}
        className={`relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start reveal ${inView ? "in-view" : ""}`}
      >
        {/* LEFT — message + trust signals */}
        <div className="lg:col-span-5">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#A7CA99]/20 border border-[#A7CA99]/40 mb-7">
            <span className="w-2 h-2 rounded-full bg-[#A7CA99] animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#A7CA99] font-medium">
              {t("quickform.eyebrow")}
            </span>
          </div>

          <h2
            data-testid="quick-form-headline"
            className="font-serif text-4xl lg:text-5xl xl:text-6xl font-light tracking-tight leading-[1.05] text-[#F6F5F2]"
          >
            {t("quickform.title_l1")}
            <br />
            <span className="italic text-[#A7CA99]">{t("quickform.title_l2")}</span>
          </h2>
          <p className="mt-6 text-[#F6F5F2]/75 font-light leading-relaxed max-w-md">
            {t("quickform.intro")}
          </p>

          {/* Trust signals */}
          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            <div className="flex items-start gap-3">
              <Clock size={22} weight="thin" className="text-[#A7CA99] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm text-[#F6F5F2] font-normal">24h</div>
                <div className="text-[11px] text-[#F6F5F2]/55 uppercase tracking-wider mt-0.5">
                  {t("quickform.trust_response", "Resposta")}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck size={22} weight="thin" className="text-[#A7CA99] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm text-[#F6F5F2] font-normal">4.6 ★</div>
                <div className="text-[11px] text-[#F6F5F2]/55 uppercase tracking-wider mt-0.5">Google</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Lock size={22} weight="thin" className="text-[#A7CA99] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm text-[#F6F5F2] font-normal">100%</div>
                <div className="text-[11px] text-[#F6F5F2]/55 uppercase tracking-wider mt-0.5">
                  {t("quickform.trust_discreet", "Discreto")}
                </div>
              </div>
            </div>
          </div>

          {/* Contact strip */}
          <div className="mt-10 pt-8 border-t border-[#F6F5F2]/10 space-y-3 text-sm font-light text-[#F6F5F2]/80">
            <a
              href={`tel:${t("contact.phone_value").replace(/\s/g, "")}`}
              className="flex items-center gap-3 hover:text-[#A7CA99] transition-colors group"
            >
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#A7CA99]/80 w-20 shrink-0">
                {t("contact.phone_label")}
              </span>
              <span className="group-hover:translate-x-1 transition-transform">
                {t("contact.phone_value")}
              </span>
            </a>
            <div className="flex items-center gap-3">
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#A7CA99]/80 w-20 shrink-0">
                {t("contact.address_label")}
              </span>
              <span className="leading-snug">{t("contact.address_line")}</span>
            </div>
          </div>
        </div>

        {/* RIGHT — form card */}
        <div className="lg:col-span-7">
          <div className="relative">
            {/* decorative tag */}
            <div className="absolute -top-4 left-8 lg:left-12 z-10 inline-flex items-center gap-2 px-4 py-2 bg-[#A7CA99] text-[#1F2D1D] text-[11px] uppercase tracking-[0.22em] font-semibold rounded-sm shadow-md">
              <span>★</span> {t("quickform.badge", "Resposta em 24h")}
            </div>

            <form
              onSubmit={onSubmit}
              data-testid="quick-form"
              className="bg-[#F6F5F2] text-[#3E5239] p-8 lg:p-12 pt-12 lg:pt-14 rounded-md shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)] border border-[#E4E2DA]"
            >
              {done ? (
                <div data-testid="quick-form-success" className="py-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-[#A7CA99]/25 flex items-center justify-center mx-auto mb-6">
                    <Check size={28} weight="thin" className="text-[#3E5239]" />
                  </div>
                  <div className="overline text-[#A7CA99] mb-3">{t("form.success_eyebrow")}</div>
                  <h3 className="font-serif text-3xl font-light text-[#3E5239]">
                    {t("form.success_title")}
                  </h3>
                  <p className="mt-4 text-[#6B7A66] font-light max-w-sm mx-auto">
                    {t("form.success_msg")}
                  </p>
                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    data-testid="quick-form-reset"
                    className="mt-8 overline text-[#3E5239] border-b border-[#A7CA99] pb-1 hover:border-[#3E5239] transition-colors"
                  >
                    {t("form.success_reset")}
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-7">
                    <h3 className="font-serif text-2xl lg:text-3xl font-light text-[#3E5239]">
                      {t("nav.book")}
                    </h3>
                    <p className="text-sm text-[#6B7A66] mt-2 font-light">{t("quickform.intro")}</p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#6B7A66] font-medium mb-2">
                        {t("form.name")} <span className="text-[#A7CA99]">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        data-testid="quick-input-name"
                        value={form.name}
                        onChange={update("name")}
                        placeholder={t("form.name_ph")}
                        className="input-boxed"
                        autoComplete="name"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#6B7A66] font-medium mb-2">
                        {t("form.phone")} <span className="text-[#A7CA99]">*</span>
                      </label>
                      <input
                        type="tel"
                        required
                        data-testid="quick-input-phone"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder={t("form.phone_ph")}
                        className="input-boxed"
                        autoComplete="tel"
                      />
                    </div>
                  </div>

                  <div className="mt-5">
                    <label className="block text-[11px] uppercase tracking-[0.18em] text-[#6B7A66] font-medium mb-2">
                      {t("form.email")} <span className="text-[#A7CA99]">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      data-testid="quick-input-email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder={t("form.email_ph")}
                      className="input-boxed"
                      autoComplete="email"
                    />
                  </div>

                  {/* Booking calendar */}
                  <div className="mt-8 pt-8 border-t border-[#E4E2DA]">
                    <div className="block text-[11px] uppercase tracking-[0.18em] text-[#6B7A66] font-medium mb-2">
                      {t("form.calendly_label")}
                    </div>
                    <p className="text-xs text-[#6B7A66] font-light mb-4">
                      {t("form.calendly_help")}
                    </p>
                    <BookingCalendar
                      value={bookingValue}
                      onChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          appointment_date: v.date || "",
                          appointment_time: v.time || "",
                        }))
                      }
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="quick-form-submit"
                    className="btn-cta mt-8"
                  >
                    {submitting ? t("form.submitting") : t("form.submit_final")}
                    {!submitting && <span>→</span>}
                  </button>
                  <p className="mt-5 text-[11px] text-[#6B7A66] font-light leading-relaxed text-center flex items-center justify-center gap-2">
                    <Lock size={12} weight="thin" />
                    {t("form.consent")}
                  </p>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
